import { RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { buildWatchEnvironment, createBuildWatch, editInProgress } from "../lib/build-freshness";
import { hasUnsavedChanges } from "../lib/unsaved-changes";

const CHECK_EVERY_MS = 3 * 60_000;

/** Moves a tab to the build the server serves now: at the next navigation, on return to the tab or on the timer.
 * In the middle of an edit it only shows the bar, and the person reloads when ready. */
export function NewBuildWatch() {
  const [waiting, setWaiting] = useState(false);
  const location = useLocation();
  const { check } = useMemo(() => createBuildWatch(buildWatchEnvironment(),
    () => editInProgress(document, hasUnsavedChanges()), () => setWaiting(true)), []);
  const reload = useCallback(() => window.location.reload(), []);

  // The next navigation: a new screen is a moment with nothing in progress, and its numbers must be the new build's.
  const firstLocation = useRef(true);
  useEffect(() => {
    if (firstLocation.current) { firstLocation.current = false; return; }
    void check();
  }, [location.pathname, location.search, check]);

  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") void check(); };
    const onFocus = () => { void check(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    const timer = window.setInterval(() => { void check(); }, CHECK_EVERY_MS);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
      window.clearInterval(timer);
    };
  }, [check]);

  if (!waiting) return null;
  return <NewBuildBar onReload={reload} />;
}

export function NewBuildBar({ onReload }: { onReload: () => void }) {
  return (
    <div className="new-build-bar" role="status">
      <span>Вышла новая версия</span>
      <button type="button" className="new-build-bar__action" onClick={onReload}>
        <RefreshCw aria-hidden="true" size={15} />
        Обновить
      </button>
    </div>
  );
}
